import React, { useState } from "react";
import { useAuth } from "../../lib/auth";
import { uploadImage } from "../../lib/storage";
import { getImageUrl } from "../../lib/api";
import { Upload, X, AlertCircle, CheckCircle2, Image as ImageIcon } from "lucide-react";

// Basic single-line input
export function Field({ label, value, onChange, type = "text", placeholder, testid, full, required, hint }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      {label && <label className="block text-sm font-display text-inkMuted mb-2">{label}</label>}
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
        data-testid={testid}
      />
      {hint && <p className="text-xs text-inkMuted mt-1">{hint}</p>}
    </div>
  );
}

// Multi-line textarea
export function TextArea({ label, value, onChange, rows = 4, placeholder, testid, full, hint }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      {label && <label className="block text-sm font-display text-inkMuted mb-2">{label}</label>}
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
        data-testid={testid}
      />
      {hint && <p className="text-xs text-inkMuted mt-1">{hint}</p>}
    </div>
  );
}

// Text list editor (one item per line)
export function LinesField({ label, value, onChange, rows = 4, placeholder, testid, full, hint }) {
  return (
    <TextArea
      full={full}
      label={label}
      rows={rows}
      placeholder={placeholder}
      testid={testid}
      value={(value || []).join("\n")}
      hint={hint || "هر خط یک مورد"}
      onChange={(v) => {
        const lines = v.split("\n").map((s) => s.replace(/\s+$/, ""));
        // Remove trailing empty lines but preserve empty lines in the middle
        while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
        onChange(lines);
      }}
    />
  );
}

// Single-image upload field (hero bg, story image, category image, etc.)
export function ImageField({ label, value, onChange, testid, full, hint }) {
  const { r2 } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const RESIZE_OPTS = { maxWidth: 1600, maxHeight: 1600, quality: 0.82 };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const res = await uploadImage(r2, file, "images", RESIZE_OPTS);
      onChange({ url: res.url, thumb: res.thumbUrl });
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const previewSrc = getImageUrl(value, "thumb");
  const rawUrl = typeof value === "string" ? value : value?.url || "";

  return (
    <div className={full ? "md:col-span-2" : ""}>
      {label && <label className="block text-sm font-display text-inkMuted mb-2">{label}</label>}
      <div className="flex items-start gap-4 flex-wrap">
        {previewSrc ? (
          <div className="relative w-32 h-32 rounded-xl overflow-hidden group bg-honey-50 border border-honey-200">
            <img src={previewSrc} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-1 left-1 w-7 h-7 rounded-full bg-ink/75 text-paper grid place-items-center"
              data-testid={testid ? `${testid}-remove` : undefined}
              aria-label="حذف تصویر"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : null}
        <label className="w-32 h-32 rounded-xl border-2 border-dashed border-honey-300 grid place-items-center text-honey-600 cursor-pointer hover:border-honey-500">
          {busy ? (
            <span className="text-xs">در حال آپلود...</span>
          ) : (
            <span className="flex flex-col items-center gap-1 text-xs">
              <Upload className="w-4 h-4" />
              {value ? "تعویض تصویر" : "آپلود تصویر"}
            </span>
          )}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            data-testid={testid}
          />
        </label>
        <div className="flex-1 min-w-[240px]">
          <Field
            label=""
            value={rawUrl}
            onChange={(v) => onChange(v ? { url: v, thumb: v } : "")}
            placeholder="یا آدرس کامل (URL) تصویر را اینجا وارد کنید"
          />
          {hint && (
            <p className="text-xs text-inkMuted mt-2 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> {hint}
            </p>
          )}
        </div>
      </div>
      {err && (
        <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {err}
        </div>
      )}
    </div>
  );
}

// Status/error message pill
export function Msg({ msg }) {
  if (!msg) return <span />;
  const ok = msg.includes("✓");
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-olive-600" : "text-red-600"}`}>
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      <span>{msg}</span>
    </div>
  );
}

// Section card wrapper
export function Section({ title, icon: Icon, children, desc }) {
  return (
    <section className="bg-white border border-honey-200 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-1">
        {Icon && <Icon className="w-5 h-5 text-honey-500" />}
        <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
      </div>
      {desc && <p className="text-sm text-inkMuted mb-5">{desc}</p>}
      {!desc && <div className="mb-5" />}
      {children}
    </section>
  );
}

export const ICON_OPTIONS = [
  "Leaf",
  "ShieldCheck",
  "Heart",
  "Sprout",
  "Droplet",
  "Flower2",
  "Grape",
  "Truck",
  "MessageCircle",
  "Award",
  "Sparkles",
];

export function IconSelect({ label, value, onChange, testid }) {
  return (
    <div>
      {label && <label className="block text-sm font-display text-inkMuted mb-2">{label}</label>}
      <select
        value={value || "Leaf"}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
        data-testid={testid}
      >
        {ICON_OPTIONS.map((ic) => (
          <option key={ic} value={ic}>
            {ic}
          </option>
        ))}
      </select>
    </div>
  );
}
